import { useEffect, useMemo, useState } from "react";
import { MessageCircle, Check } from "lucide-react";
import { eur, fmtDate } from "@/lib/api";
import { BRAND, LISTINO, SERVICES, waLink } from "@/lib/brand";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/common";

const IVA = 22;
const REVIEW = "https://g.page/r/Ce-fM59YifubEBM/review";
const COLLETTIVE = [
  { id: "c1", label: "Cremazione collettiva – fino a 10 kg", price: 120 },
  { id: "c2", label: "Cremazione collettiva – da 11 a 30 kg", price: 160 },
  { id: "c3", label: "Cremazione collettiva – oltre 30 kg", price: 200 },
];

const buildMsg = (p, costo, note) => {
  const nome = (p.nome || "").trim();
  const tot = costo ? costo * (1 + IVA / 100) : p.totale || 0;
  const residuo = Math.max(tot - (p.acconto || 0) - (p.saldo || 0), 0);
  const L = [
    `Gentile ${nome},`,
    ``,
    `grazie di cuore per aver scelto ${BRAND.name} in un momento così delicato. Ci prenderemo cura di ${p.animale_nome || "del suo piccolo amico"} con il massimo rispetto.`,
    ``,
    `📋 *Riepilogo del servizio*`,
    `• Animale: ${p.animale_nome || "—"}${p.specie ? ` (${p.specie})` : ""}`,
    `• Servizio: ${SERVICES[p.servizio] || p.servizio}`,
    p.data_ritiro ? `• Ritiro: ${fmtDate(p.data_ritiro)}` : null,
    `• Costo servizio: ${eur(tot)} (IVA inclusa)`,
    p.acconto ? `• Acconto ricevuto: ${eur(p.acconto)}` : null,
    `• Saldo da versare: ${eur(residuo)}`,
    ``,
    `Il saldo può essere versato alla riconsegna, in contanti, carta o bancomat.`,
    note ? `` : null, note || null,
    ``,
    `📱 *Scarichi la nostra App*: ${window.location.origin}/app`,
    `Potrà seguire la cremazione, ricevere il video ricordo e lasciare una dedica nel Cimitero virtuale.`,
    ``,
    `⭐ Se lo desidera, una recensione ci aiuta molto: ${REVIEW}`,
    ``,
    `Restiamo a sua completa disposizione.`,
    `${BRAND.name} – ${BRAND.phone}`,
  ];
  return L.filter((x) => x !== null).join("\n");
};

const NUOVO = { nome: "", telefono: "", animale_nome: "", specie: "", servizio: "singola", data_ritiro: "", acconto: 0, saldo: 0, totale: 0 };

export const PromemoriaDialog = ({ pratica, open, onOpenChange }) => {
  const [sel, setSel] = useState(null);
  const [custom, setCustom] = useState("");
  const [note, setNote] = useState("");
  const [p, setP] = useState(null);
  const libero = !pratica?.id;
  useEffect(() => { if (open) { setSel(null); setCustom(""); setNote(""); setP(pratica?.id ? pratica : { ...NUOVO, ...(pratica || {}) }); } }, [open, pratica]);
  const setPf = (k) => (e) => setP((x) => ({ ...x, [k]: e.target.value }));
  const costo = custom !== "" ? Number(custom) : sel?.price ?? null;
  const msg = useMemo(() => (p ? buildMsg(p, costo, note) : ""), [p, costo, note]);
  if (!p) return null;
  const rows = p.servizio === "singola" ? LISTINO.filter((t) => t.price) : COLLETTIVE;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#FAF8F5] max-h-[90vh] overflow-y-auto" data-testid="promemoria-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0D3B52]">Promemoria WhatsApp{p.nome ? ` – ${p.nome}` : " – nuovo cliente"}</DialogTitle>
          <DialogDescription>Spunta il costo del servizio: preparo il messaggio con ringraziamenti, dettagli, saldo e link per scaricare l'app.</DialogDescription>
        </DialogHeader>
        {libero && (
          <div className="grid sm:grid-cols-3 gap-3 rounded-lg border border-[#E2DDD5] bg-white/70 p-3" data-testid="promemoria-libero-form">
            <Field label="Nome cliente *"><Input value={p.nome} onChange={setPf("nome")} className="bg-white" data-testid="promemoria-nome" /></Field>
            <Field label="Telefono WhatsApp *"><Input value={p.telefono} onChange={setPf("telefono")} placeholder="3xx xxx xxxx" className="bg-white" data-testid="promemoria-telefono" /></Field>
            <Field label="Servizio">
              <select value={p.servizio} onChange={setPf("servizio")} className="flex h-10 w-full rounded-md border border-[#E2DDD5] bg-white px-3 text-sm" data-testid="promemoria-servizio">
                <option value="singola">Cremazione singola</option><option value="multipla">Cremazione collettiva</option>
              </select>
            </Field>
            <Field label="Nome animale"><Input value={p.animale_nome} onChange={setPf("animale_nome")} className="bg-white" data-testid="promemoria-animale" /></Field>
            <Field label="Specie"><Input value={p.specie} onChange={setPf("specie")} placeholder="Cane, Gatto…" className="bg-white" /></Field>
            <Field label="Data ritiro"><Input type="date" value={p.data_ritiro} onChange={setPf("data_ritiro")} className="bg-white" /></Field>
            <Field label="Acconto ricevuto €"><Input type="number" step="0.01" value={p.acconto || ""} onChange={(e) => setP((x) => ({ ...x, acconto: Number(e.target.value) || 0 }))} className="bg-white" /></Field>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#997316]">{p.servizio === "singola" ? "Cremazioni singole (IVA escl.)" : "Cremazioni collettive (IVA escl.)"}</p>
            {rows.map((t) => (
              <button key={t.id} type="button" onClick={() => { setSel(t); setCustom(""); }} data-testid={`promemoria-costo-${t.id}`}
                className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors ${sel?.id === t.id && custom === "" ? "border-[#D4AF37] bg-[#FBF6E8]" : "border-[#E2DDD5] bg-white hover:border-[#D4AF37]"}`}>
                <span className="text-left">{t.label}{t.range ? <span className="block text-xs text-[#5C6B73]">{t.range}</span> : null}</span>
                <span className="font-semibold text-[#0D3B52] flex items-center gap-1">{eur(t.price)}{sel?.id === t.id && custom === "" && <Check className="h-4 w-4 text-[#997316]" />}</span>
              </button>
            ))}
            <Field label="Oppure costo personalizzato € (IVA escl.)"><Input type="number" step="0.01" value={custom} onChange={(e) => setCustom(e.target.value)} className="bg-white" data-testid="promemoria-custom" /></Field>
            <Field label="Nota aggiuntiva (opz.)"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="bg-white" /></Field>
          </div>
          <div className="space-y-3">
            <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#997316]">Anteprima messaggio</p>
            <pre className="whitespace-pre-wrap text-xs bg-white border border-[#E2DDD5] rounded-lg p-3 font-sans leading-relaxed max-h-80 overflow-y-auto" data-testid="promemoria-preview">{msg}</pre>
            <a href={waLink(p.telefono, msg)} target="_blank" rel="noopener noreferrer" data-testid="promemoria-send"
              className={`inline-flex w-full justify-center items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white ${p.telefono ? "bg-[#25D366] hover:bg-[#1DA851]" : "bg-gray-300 pointer-events-none"}`}>
              <MessageCircle className="h-4 w-4" />{p.telefono ? `Invia su WhatsApp a ${p.telefono}` : "Nessun telefono in anagrafica"}
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
